import React, { ReactElement } from 'react'

import { ReportTable } from '../../elements/ReportTable'
import { TableHeader } from '../../elements/TableHeader'
import { SkeletonCell } from './elements/SkeletonCell'
import * as Styled from './styles'

const FIRST_COLUMN_ADJUSTMENTS = {
  groupRow: -20,
  accountRow: 20,
} as const

interface ReportSkeletonProps {
  headerChildren: ReactElement
  columnWidths: number[]
  groupRowsCount?: number
  accountRowsCount?: number
}

export const ReportSkeleton = ({
  headerChildren,
  columnWidths,
  groupRowsCount = 5,
  accountRowsCount = 12,
}: ReportSkeletonProps): ReactElement => {
  const renderSkeletonRow = (widths: number[]) => (
    <tr>
      {widths.map((width, index) => (
        <Styled.SkeletonCell key={index}>
          <SkeletonCell width={width} colIndex={index} />
        </Styled.SkeletonCell>
      ))}
    </tr>
  )

  const renderHeaderRow = (widths: number[]) => (
    <tr>
      {widths.map((width, index) => (
        <Styled.SkeletonHeaderCell key={index}>
          <SkeletonCell width={width} colIndex={index} />
        </Styled.SkeletonHeaderCell>
      ))}
    </tr>
  )

  const adjustFirstColumn = (baseWidths: number[], adjustment: number) =>
    baseWidths.map((width, index) => (index === 0 ? width + adjustment : width))

  const headerRow = renderHeaderRow(columnWidths)
  const groupRows = Array.from({ length: groupRowsCount }).map(() =>
    renderSkeletonRow(adjustFirstColumn(columnWidths, FIRST_COLUMN_ADJUSTMENTS.groupRow)),
  )
  const accountRows = Array.from({ length: accountRowsCount }).map(() =>
    renderSkeletonRow(adjustFirstColumn(columnWidths, FIRST_COLUMN_ADJUSTMENTS.accountRow)),
  )

  return (
    <ReportTable allExpandableIds={[]}>
      <TableHeader>{headerChildren}</TableHeader>
      <tbody>
        {headerRow}
        {groupRows}
        {accountRows}
      </tbody>
    </ReportTable>
  )
}
